import { useFormContext } from 'react-hook-form';
import { CharacterCounter } from '@/components/common/CharacterCounter';
import { DatePickerInput } from '@/components/common/DatePickerInput';
import { FIELD_LABEL_CLASS } from '@/components/common/FormDialog/formDialog.constants';
import { LabelText } from '@/components/common/LabelText';
import { FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { cn } from '@/lib/utils';
import { FIELD_CONTROL_CLASS, TEXTAREA_CONTROL_CLASS } from '@/styles/fieldControl';
import {
  SURVEY_DESCRIPTION_MAX_LENGTH,
  SURVEY_TITLE_MAX_LENGTH,
} from '../../constants/survey.constants';
import type { CreateSurveyFormValues } from '../../types/survey.types';
import { SurveyFormSection } from './primitives/SurveyFormSection';

const HEADING_ID = 'survey-basic-info-heading';
const DATE_FIELDS = [
  { name: 'startDate', id: 'survey-start-date', label: 'Start Date' },
  { name: 'endDate', id: 'survey-end-date', label: 'End Date' },
] as const;

export function SurveyBasicInfoSection() {
  const { control } = useFormContext<CreateSurveyFormValues>();

  return (
    <SurveyFormSection ariaLabelledBy={HEADING_ID}>
      <div>
        <h2 id={HEADING_ID} className="text-lg font-semibold">
          Basic Info
        </h2>
      </div>

      <FormField
        control={control}
        name="title"
        render={({ field, fieldState }) => (
          <FormItem>
            <FormLabel className={FIELD_LABEL_CLASS}>
              <LabelText isRequired>Title</LabelText>
            </FormLabel>
            <FormControl>
              <Input
                {...field}
                placeholder="e.g., Monthly Field Visit Survey"
                className={FIELD_CONTROL_CLASS}
                maxLength={SURVEY_TITLE_MAX_LENGTH}
                aria-required="true"
                aria-invalid={fieldState.invalid || undefined}
              />
            </FormControl>
            <div className="flex items-center justify-between gap-2">
              <FormMessage />
              <CharacterCounter
                current={field.value?.length ?? 0}
                max={SURVEY_TITLE_MAX_LENGTH}
                className="ml-auto"
              />
            </div>
          </FormItem>
        )}
      />

      <FormField
        control={control}
        name="description"
        render={({ field, fieldState }) => (
          <FormItem>
            <FormLabel className={FIELD_LABEL_CLASS}>Description</FormLabel>
            <FormControl>
              <Textarea
                {...field}
                value={field.value ?? ''}
                rows={3}
                placeholder="Short context about this survey."
                className={TEXTAREA_CONTROL_CLASS}
                maxLength={SURVEY_DESCRIPTION_MAX_LENGTH}
                aria-invalid={fieldState.invalid || undefined}
              />
            </FormControl>
            <div className="flex items-center justify-between gap-2">
              <FormMessage />
              <CharacterCounter
                current={field.value?.length ?? 0}
                max={SURVEY_DESCRIPTION_MAX_LENGTH}
                className="ml-auto"
              />
            </div>
          </FormItem>
        )}
      />

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        {DATE_FIELDS.map((dateField) => (
          <FormField
            key={dateField.name}
            control={control}
            name={dateField.name}
            render={({ field, fieldState }) => (
              <FormItem>
                <FormLabel className={FIELD_LABEL_CLASS}>
                  <LabelText isRequired>{dateField.label}</LabelText>
                </FormLabel>
                <FormControl>
                  <DatePickerInput
                    id={dateField.id}
                    value={field.value}
                    onChange={field.onChange}
                    className={cn(FIELD_CONTROL_CLASS, 'sm:h-10')}
                    {...(fieldState.invalid ? { 'aria-invalid': true } : {})}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        ))}
      </div>
    </SurveyFormSection>
  );
}
